"use client";

import Link from "next/link";
import Image from "next/image";
import { Icon } from "@/components/ui/Icon";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import "@/styles/splitscreen.scss";
import StarBorder from "@/components/react-bits/StarBorder";
import { cn } from "@/utils/cn";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { LANDING, href } from "./landingDict";

import reservation from "@/assets/images/hero/chauffeur-prive5.png";
import rental from "@/assets/images/hero/hero-location.webp";

type HeroSectionProps = {
  locale?: Locale;
};

export function HeroSection({ locale = "fr" }: HeroSectionProps) {
  const t = tr(LANDING, locale).hero;
  const driverHref = href(locale, "/reservation");
  const rentalHref = href(locale, "/location/vehicules#catalogue-location");
  const smoothEase = [0.22, 1, 0.36, 1] as const;
  const [hoveredSection, setHoveredSection] = useState<
    "none" | "driver" | "rental"
  >("none");
  const [isDriverLinkHovered, setIsDriverLinkHovered] = useState(false);
  const [isRentalLinkHovered, setIsRentalLinkHovered] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [isReservationVideoReady, setIsReservationVideoReady] = useState(false);
  const [isReservationVideoError, setIsReservationVideoError] = useState(false);
  const reservationVideoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowScrollHint(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const media = reservationVideoRef.current;
    if (!media) return;

    // Double verrouillage audio pour éviter tout son en prod.
    media.defaultMuted = true;
    media.muted = true;
    media.volume = 0;
  }, []);

  const handleScrollDown = () => {
    try {
      const target = (window.scrollY - 80 || 0) + window.innerHeight;
      window.scrollTo({ top: target, behavior: "smooth" });
    } catch {}
  };

  const fadeVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      filter: "blur(10px)",
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: {
        duration: 0.5,
        ease: smoothEase,
      },
    },
  };

  const isDriverActive = hoveredSection === "driver" || isDriverLinkHovered;
  const isRentalActive = hoveredSection === "rental" || isRentalLinkHovered;

  return (
    <section className="relative h-screen w-full overflow-hidden">
      <section className="split-screen-wrapper">
        <main
          className={cn(
            "this transition-colors",
            "border-b lg:border-b-0 lg:border-e",
            hoveredSection === "driver"
              ? "border-neutral-500"
              : "border-transparent",
          )}
          onMouseEnter={() => setHoveredSection("driver")}
          onMouseLeave={() => setHoveredSection("none")}
        >
          <div className="this-image relative overflow-x-hidden">
            <Image
              src={reservation}
              alt={t.driverImageAlt}
              fill
              priority
              fetchPriority="high"
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover"
            />
            <video
              ref={reservationVideoRef}
              className={`pointer-events-none absolute inset-0 z-[4] h-full w-full object-cover transition-opacity duration-500 ${
                isReservationVideoReady && !isReservationVideoError
                  ? "opacity-100"
                  : "opacity-0"
              }`}
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              aria-hidden
              onCanPlay={(event) => {
                const media = event.currentTarget;
                media.defaultMuted = true;
                media.muted = true;
                media.volume = 0;
                media
                  .play()
                  .then(() => {
                    setIsReservationVideoReady(true);
                    setIsReservationVideoError(false);
                  })
                  .catch(() => {
                    setIsReservationVideoReady(false);
                    setIsReservationVideoError(true);
                  });
              }}
              onLoadedData={() => {
                const media = reservationVideoRef.current;
                if (media) {
                  media.defaultMuted = true;
                  media.muted = true;
                  media.volume = 0;
                }
                setIsReservationVideoReady(true);
                setIsReservationVideoError(false);
              }}
              onVolumeChange={(event) => {
                const media = event.currentTarget;
                if (!media.muted || media.volume !== 0) {
                  media.defaultMuted = true;
                  media.muted = true;
                  media.volume = 0;
                }
              }}
              onError={() => {
                setIsReservationVideoReady(false);
                setIsReservationVideoError(true);
              }}
            >
              <source
                src="/video/Reservez-votre-Mercedes-Classe-V-avec-Chauffeur-Prive-Plongez-dans-le-glamour-de-Cannes-en-s.mp4"
                type="video/mp4"
              />
            </video>
            <div
              className={cn(
                "pointer-events-none absolute inset-0 z-[5] bg-black transition-opacity duration-500",
                hoveredSection === "driver"
                  ? "opacity-30"
                  : hoveredSection === "rental"
                    ? "opacity-70"
                    : "opacity-50",
              )}
            />
          </div>

          <div className="this-content">
            <div className="this-title">
              <motion.h2
                animate={{
                  scale: hoveredSection === "driver" ? 1.1 : 1,
                  y: hoveredSection === "driver" ? -20 : 0,
                }}
                transition={{ duration: 0.5, ease: smoothEase }}
              >
                <span className="relative inline-block text-center">
                  <Link
                    href={driverHref}
                    onMouseEnter={() => setIsDriverLinkHovered(true)}
                    onMouseLeave={() => setIsDriverLinkHovered(false)}
                    className={cn(
                      "transition-colors duration-300",
                      isDriverActive ? "!text-primary" : "text-white",
                    )}
                  >
                    {t.driverTitle}
                  </Link>
                  <motion.span
                    aria-hidden
                    className="pointer-events-none absolute bottom-1 left-0 h-[2px] w-full origin-left rounded-full bg-primary"
                    initial={false}
                    animate={{
                      scaleX: isDriverActive ? 1 : 0,
                      opacity: isDriverActive ? 1 : 0,
                    }}
                    transition={{ duration: 0.35, ease: smoothEase }}
                  />
                </span>
              </motion.h2>
            </div>
            <AnimatePresence>
              {hoveredSection === "driver" && (
                <motion.div
                  className="this-description"
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={fadeVariants}
                >
                  <p
                    className={cn(
                      "opacity-0 transition-opacity duration-700",
                      hoveredSection === "driver" && "opacity-100",
                    )}
                  >
                    {t.driverDescription}
                  </p>
                  <StarBorder as="button" color="primary" speed="4s">
                    <Link href={driverHref}>{t.driverCta}</Link>
                  </StarBorder>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </main>

        <main
          className={cn(
            "this transition-colors",
            "border-t lg:border-s lg:border-t-0",
            hoveredSection === "rental"
              ? "border-neutral-500"
              : "border-transparent",
          )}
          onMouseEnter={() => setHoveredSection("rental")}
          onMouseLeave={() => setHoveredSection("none")}
        >
          <div className="this-image relative overflow-x-hidden">
            <Image
              src={rental}
              alt={t.rentalImageAlt}
              fill
              priority
              fetchPriority="high"
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover"
            />
            <div
              className={cn(
                "pointer-events-none absolute inset-0 z-[5] bg-black transition-opacity duration-500",
                hoveredSection === "rental"
                  ? "opacity-30"
                  : hoveredSection === "driver"
                    ? "opacity-70"
                    : "opacity-50",
              )}
            />
          </div>

          <div className="this-content">
            <div className="this-title">
              <motion.h2
                animate={{
                  scale: hoveredSection === "rental" ? 1.1 : 1,
                  y: hoveredSection === "rental" ? -20 : 0,
                }}
                transition={{ duration: 0.5, ease: smoothEase }}
              >
                <span className="relative inline-block text-center">
                  <Link
                    href={rentalHref}
                    onMouseEnter={() => setIsRentalLinkHovered(true)}
                    onMouseLeave={() => setIsRentalLinkHovered(false)}
                    className={cn(
                      "transition-colors duration-300",
                      isRentalActive ? "!text-primary" : "text-white",
                    )}
                  >
                    {t.rentalTitle}
                  </Link>
                  <motion.span
                    aria-hidden
                    className="pointer-events-none absolute bottom-1 left-0 h-[2px] w-full origin-left rounded-full bg-primary"
                    initial={false}
                    animate={{
                      scaleX: isRentalActive ? 1 : 0,
                      opacity: isRentalActive ? 1 : 0,
                    }}
                    transition={{ duration: 0.35, ease: smoothEase }}
                  />
                </span>
              </motion.h2>
            </div>
            <AnimatePresence>
              {hoveredSection === "rental" && (
                <motion.div
                  className="this-description"
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={fadeVariants}
                >
                  <p
                    className={cn(
                      "opacity-0 transition-opacity duration-700",
                      hoveredSection === "rental" && "opacity-100",
                    )}
                  >
                    {t.rentalDescription}
                  </p>
                  <StarBorder as="button" color="white" speed="4s">
                    <Link href={rentalHref}>{t.rentalCta}</Link>
                  </StarBorder>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </main>
        {/* Indicateur de scroll */}
        <AnimatePresence>
          {showScrollHint && (
            <motion.div
              key="scroll-hint"
              className="pointer-events-none absolute inset-x-0 bottom-6 z-[20] flex items-center justify-center"
              initial={false}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
            >
              <motion.button
                type="button"
                aria-label={t.scrollHint}
                onClick={handleScrollDown}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleScrollDown();
                  }
                }}
                className="pointer-events-auto rounded-full bg-black/40 p-2 shadow-md backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary"
                initial={{ y: 0 }}
                animate={{ y: [0, 8, 0] }}
                transition={{
                  duration: 1.6,
                  repeat: Infinity,
                  ease: smoothEase,
                }}
              >
                <Icon icon="mdi:chevron-down" className="h-6 w-6 text-white" />
                <span className="sr-only">{t.scrollHint}</span>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </section>
  );
}
